import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format currency
export function formatCurrency(
  value: number, 
  options: { 
    currency?: string
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {}
): string {
  const { 
    currency = 'USD',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2 
  } = options
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value)
}

// Format number
export function formatNumber(
  value: number,
  options: {
    decimals?: number
    compact?: boolean
  } = {}
): string {
  const { decimals = 2, compact = false } = options
  
  if (compact && value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (compact && value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

// Format percentage
export function formatPercentage(value: number, includeSign = true): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: includeSign ? 'exceptZero' : 'never',
  }).format(value / 100)
  
  return formatted
}

// Format date
export function formatDate(
  date: string | Date,
  options: { 
    format?: 'short' | 'long' | 'relative'
    includeTime?: boolean
  } = {}
): string {
  const { format = 'short', includeTime = false } = options
  const d = typeof date === 'string' ? new Date(date) : date
  
  if (format === 'relative') {
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: format === 'long' ? 'long' : 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  }
  
  if (includeTime) {
    dateOptions.hour = '2-digit'
    dateOptions.minute = '2-digit'
  }
  
  return d.toLocaleDateString('en-US', dateOptions)
}

// Format address (truncate)
export function formatAddress(address: string, chars = 6): string {
  if (!address) return ''
  if (address.length <= chars * 2 + 2) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

// Mask card number
export function maskCardNumber(lastFour: string): string {
  return `•••• ${lastFour}`
}

// Truncate text
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

// Get initials from name
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Get color for change
export function getChangeColor(value: number): string {
  if (value > 0) return 'text-accent-green'
  if (value < 0) return 'text-accent-red'
  return 'text-text-secondary'
}

// Get gradient for card
export function getCardGradient(color: string): string {
  const gradients: Record<string, string> = {
    purple: 'from-purple-500 to-violet-600',
    blue: 'from-blue-500 to-blue-600',
    cyan: 'from-cyan-500 to-teal-500',
    green: 'from-emerald-500 to-green-600',
    orange: 'from-orange-500 to-amber-500',
    pink: 'from-pink-500 to-rose-500',
  }
  return gradients[color] || gradients.purple
}

// Copy to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('Failed to copy:', err)
    return false
  }
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Sleep utility
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}